import { CircularProgress, Stack, Typography } from "@mui/material";
import { CustomButton } from "../../ui/CustomButton";
import { MingcutePdfIcon } from "../../images";
import {
  useGeneratePlaybookPDFMutation,
  useGetSectionConfigQuery,
} from "../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../../ui/CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useEffect, useState } from "react";
import { ReuseManageSectionsModal } from "../../ui/modal/ReuseManageSectionModal";
import { SectionItemType } from "../../../types/apiTypes";
import { Playbook } from "../../../types";
import { CopyUrlBtn } from "../../ui/buttons/CopyUrlBtn/CopyUrlBtn";
import { AppRoutes, PlaybookPlanNames } from "../../../types/enums";
import { useAppSelector } from "../../../hooks";
import { selectPermissions } from "../../../features/permissionsSlice";

const defaultSections = {
  read_first: {
    value: {},
    checked: true,
    label: "Understanding the Prospect Type",
  },
  do_dont: {
    checked: true,
    label: "Do's and Dont's for Engaging Intent-Based Prospect",
    value: {},
  },
  lead_details: {
    checked: true,
    label: "Prospect Details",
    value: {},
  },
  outreach_research: {
    value: {
      engagement_signals_analysis: {
        value: {},
        checked: true,
        label: "Prospect-Provided Intelligence",
      },
      partner_enrichment_data_analysis: {
        value: {},
        checked: true,
        label: "Partner/Enrichment Data Analysis",
      },
      linkedin_profile_analysis: {
        value: {},
        checked: true,
        label: "LinkedIn Profile Analysis",
      },
    },
    checked: true,
    label: "Part 1: Immediate Prep + Fast Track Outreach (Day 0-3)",
  },
  outbound_sequence: {
    value: {},
    checked: true,
    label: "Part 2: 7-Day Outbound Sequence (Day 3–10)",
  },
  first_call: {
    value: {
      call_intro: {
        value: {},
        checked: true,
        label: "Intro (30–45 seconds)",
      },
      call_contextual_hook: {
        value: {},
        checked: true,
        label: "Contextual Hook (30–45 seconds)",
      },
      call_discovery_questions: {
        value: {},
        checked: true,
        label: "Discovery Questions (Choose 2–3 to Ask)",
      },
      call_value_alignment: {
        value: {},
        checked: true,
        label:
          "Value Alignment + Soft Pitch (30 seconds) - How Your Solution Addresses Their Goals",
      },
      call_close: {
        value: {},
        checked: true,
        label: "Close — Offer Time Slots for an Intro Conversation",
      },
    },
    checked: true,
    label: "SDR First Call Script",
  },
  amnesia_best_practices: {
    value: {},
    checked: true,
    label: "Prospect Amnesia Best Practices",
  },
  crm_notes: {
    value: {},
    checked: true,
    label: "СRM Notes to Capture",
  },
};

export const PlaybookHeader = ({
  isDemo,
  isFixed,
  isPublic,
  companyName,
  leadName,
  campaign_id,
  member_id,
  companyType,
  isMock,
  playbook,
}: {
  isDemo?: boolean;
  isFixed: boolean;
  isPublic?: boolean;
  companyName: string;
  leadName: string;
  campaign_id: string;
  member_id: string;
  companyType?: string | string[];
  isMock?: boolean;
  playbook?: Playbook;
}) => {
  const { isAllowedExportPlaybooks } = useAppSelector(selectPermissions);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedSections, setSelectedSections] = useState<
    Record<string, SectionItemType>
  >({});

  const { data: sections, isLoading: isSectionsLoading } =
    useGetSectionConfigQuery(
      {
        campaign_id,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !isOpen || isMock || !campaign_id,
      },
    );

  useEffect(() => {
    if (sections && !isMock) {
      const currentSections = Object.entries(
        sections?.sdr_playbook_fields || {},
      ).reduce((acc, [key, item]) => {
        if (item.checked) {
          return { ...acc, [key]: item };
        }

        const values = Object.entries(item.value || {});

        if (values.length > 0) {
          let hasSelected = false;
          const filtered = values.reduce((subAcc, [subKey, subItem]) => {
            if (!subItem.checked) return subAcc;
            hasSelected = true;
            return { ...subAcc, [subKey]: subItem };
          }, {});

          return hasSelected
            ? { ...acc, [key]: { ...item, value: filtered, checked: true } }
            : acc;
        }

        return acc;
      }, {});

      setSelectedSections(currentSections);
    } else {
      setSelectedSections(structuredClone(defaultSections));
    }
  }, [sections]);

  const [generatePlaybook, { isLoading }] = useGeneratePlaybookPDFMutation();

  const onCloseHandler = () => {
    setIsOpen(false);
  };

  const downloadHandler = (
    selectedSections: Record<string, SectionItemType>,
  ) => {
    const topics = structuredClone(playbook) as Playbook;

    for (const topicKey in selectedSections) {
      const entry = Object.entries(selectedSections[topicKey].value);
      if (entry.length > 0 && !selectedSections[topicKey].checked) {
        let hasChildren = false;
        entry.forEach(([subKey, item]) => {
          if (!item.checked) {
            // @ts-ignore
            topics[topicKey][subKey] = undefined;
            return;
          }
          hasChildren = true;
        });

        if (!hasChildren) {
          // @ts-ignore
          topics[topicKey] = undefined;
        }
      }

      if (entry.length === 0 && !selectedSections[topicKey].checked) {
        // @ts-ignore
        topics[topicKey] = undefined;
      }
    }

    if (!topics.read_first) {
      topics.description = [];
    }
    onCloseHandler();
    generatePlaybook({ member_id, topics, campaign_id })
      .unwrap()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${PlaybookPlanNames.playbook} for ${companyName} - ${leadName}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={`Failed to generate ${PlaybookPlanNames.playbook} PDF`}
          />,
        );
      });
  };

  const onClickHandler = () => {
    setIsOpen(true);
  };

  return (
    <Stack
      sx={
        isFixed
          ? {
              position: "fixed",
              top: `${isPublic ? 74 : isDemo ? 100 : 72}px`,
              left: 0,
              backgroundColor: "transparent",
              width: "100%",
              zIndex: 2,
            }
          : undefined
      }
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        sx={{
          padding: "24px 32px",
          background:
            "linear-gradient(267.05deg, #1C3C70 -0.33%, #0097DD 108.14%)",
          borderRadius: "8px",
          ...(isFixed && {
            padding: { sm: "12px 40px", lg: "12px 72px" },
            position: "sticky",
            top: "72px",
            background: "none",
            backgroundColor: "#0860A8",
            width: "100vw",
            zIndex: 2,
            borderRadius: "none",
          }),
        }}
      >
        <Stack alignItems="flex-start" gap={isFixed ? "8px" : "16px"}>
          <Stack
            alignItems="flex-start"
            flexDirection={isFixed ? "row" : "column"}
            gap={isFixed ? "4px" : undefined}
          >
            <Typography
              variant="h1"
              color={"white"}
              fontSize={!isFixed ? "32px" : "18px"}
              lineHeight={!isFixed ? "44px" : "22px"}
            >
              {PlaybookPlanNames.playbook} for
            </Typography>
            <Typography
              variant="h1"
              color={"white"}
              fontSize={!isFixed ? "32px" : "18px"}
              lineHeight={!isFixed ? "44px" : "22px"}
              fontWeight={700}
            >
              {companyName}: {leadName}
            </Typography>
          </Stack>

          {companyType && (
            <Stack flexDirection={"row"} gap={"8px"} flexWrap={"wrap"}>
              {(typeof companyType === "string"
                ? [companyType]
                : companyType
              ).map((type) => (
                <Typography
                  key={type}
                  component="span"
                  sx={{
                    borderRadius: "4px",
                    backgroundColor: "#CEDFEE",
                    padding: isFixed ? "4px 8px" : "0 12px",
                    color: "#032643",
                    fontSize: isFixed ? "12px" : "14px",
                    lineHeight: isFixed ? "12px" : "26px",
                    fontWeight: 500,
                  }}
                >
                  {type}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack
          gap={isFixed ? "8px" : "12px"}
          flexDirection={isFixed ? "row" : "column"}
        >
          {isAllowedExportPlaybooks && !playbook?.is_white_label && (
            <CustomButton
              onClick={onClickHandler}
              variant="outlined"
              textColor="white"
              fullWidth={true}
              type="button"
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} sx={{ color: "inherit" }} />
                ) : (
                  <MingcutePdfIcon color="white" />
                )
              }
              disabled={isLoading}
              sx={{
                borderColor: "white",
                backgroundColor: "transparent",
                "&.Mui-disabled": {
                  color: "white",
                  borderColor: "white",
                },
                "&.Mui-disabled svg": {
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "white",
                },
              }}
            >
              Export PDF
            </CustomButton>
          )}
          <CopyUrlBtn
            url={`${process.env.REACT_APP_BASE_URL}${AppRoutes.PLAYBOOK}/${campaign_id}/${member_id}`}
          />
        </Stack>
      </Stack>
      {isOpen && (
        <ReuseManageSectionsModal
          sections={selectedSections}
          title={`Export ${PlaybookPlanNames.playbook}`}
          includeText={"Please choose sections you want to export."}
          description={""}
          submitBtnText={"Export"}
          cancelBtnText={"Cancel"}
          isLoading={isLoading || isSectionsLoading}
          onSubmit={downloadHandler}
          onClose={onCloseHandler}
          onBack={onCloseHandler}
        />
      )}
    </Stack>
  );
};
